import React, { useEffect, useState } from "react";
import "../../sass/shortcode/PatientProfile.scss";
import { FaPen, FaTimes, FaUserCircle, FaFilePdf, FaFileImage, FaFile } from "react-icons/fa";
import { Link } from "react-router-dom";
import { BASE_URL, ASSET_URL } from "../../helpers/api";
import { useRef } from "react";

export default function PatientProfile() {
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);  // ✅ single state
  const [prescriptions, setPrescriptions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState("appointments");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [prescriptionModalVisible, setPrescriptionModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) { alert("❌ No token found"); return; }

        const patientRes = await fetch(`${BASE_URL}/user/profile?token=${token}`);
        const patient = await patientRes.json();
        if (!patient) { alert("❌ No matching patient found"); return; }
        setUser(patient);
        const patient_Id = patient.patient_id;

        // ✅ single documents fetch from document table only
        const docsRes = await fetch(`${BASE_URL}/patient/documents/${patient_Id}`);
        const docsData = await docsRes.json();
        setDocuments(docsData?.documents || []);

        const rxRes = await fetch(`${BASE_URL}/patient/prescriptions/${patient_Id}`);
        const rxData = await rxRes.json();
        setPrescriptions(rxData?.prescriptions || []);

        const appointmentsRes = await fetch(`${BASE_URL}/patient/appointments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ patient_id: patient_Id }),
        });
        const appointmentsData = await appointmentsRes.json();
        setAppointments(appointmentsData.upcoming || []);
        setPastAppointments(appointmentsData.past || []);

      } catch (err) {
        console.error("Error fetching patient data:", err);
      }
    };
    fetchPatientData();
  }, []);

  const forceDownload = (url, filename) => {
  const filePath = url.replace(`${ASSET_URL}/`, '');
  const downloadUrl = `${BASE_URL}/download?file=${filePath}`;
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  const downloadPrescriptionAsPDF = (rx = selectedPrescription) => {
    if (!rx) return;
    const printContent = `
      <html><head><title>Prescription - ${formatDate(rx.date)}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 30px; color: #333; font-size: 13px; }
        h1 { text-align:center; font-size:20px; margin-bottom:4px; color:#1565c0; }
        .sub { text-align:center; color:#666; font-size:12px; margin-bottom:24px; }
        .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:16px; }
        .box { border:1px solid #ddd; border-radius:8px; padding:14px 18px; background:#f9fafb; }
        .slabel { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.8px; margin-bottom:8px; display:block; color:#555; border-bottom:1px solid #eee; padding-bottom:6px; }
        .section { margin-bottom:16px; border:1px solid #ddd; border-radius:8px; padding:14px 18px; background:#f9fafb; }
        table { width:100%; border-collapse:collapse; font-size:12px; }
        th { padding:6px 8px; background:#f0f0f0; color:#555; font-weight:600; font-size:11px; text-align:left; }
        td { padding:7px 8px; border-bottom:1px solid #f0f0f0; }
        .fees { text-align:right; padding-top:10px; font-size:12px; color:#666; border-top:1px solid #eee; margin-top:10px; }
        p { margin: 3px 0; }
      </style></head><body>
        <h1>Prescription</h1>
        <div class="sub">Rx #${prescriptions.indexOf(rx) + 1} · ${formatDate(rx.date)} · Dr. ${rx.doctor_name} · ${rx.appointment_id ? `Appt #${rx.appointment_id}` : "Direct Consultation"}</div>
        <div class="grid2">
          <div class="box">
            <span class="slabel">Doctor Info</span>
            <p><strong>Dr. ${rx.doctor_name}</strong></p>
            <p>${formatDate(rx.date)}</p>
            <p>${rx.appointment_id ? `Appt #${rx.appointment_id}` : "Direct Consultation"}</p>
          </div>
          <div class="box">
            <span class="slabel">Patient Info</span>
            <p><strong>${user?.firstname || ""} ${user?.lastname || ""}</strong></p>
            <p>DOB: ${user?.date_of_birth || "—"}</p>
            <p>Gender: ${user?.sex || "—"}</p>
            <p>Blood Group: ${user?.blood_group || "—"}</p>
          </div>
        </div>
        <div class="grid2">
          <div class="box">
            <span class="slabel">Visit Info</span>
            <p>Type: ${rx.patient_type || "—"}</p>
            <p>Weight: ${rx.weight || "—"}</p>
            <p>BP: ${rx.blood_pressure || "—"}</p>
            <p>Ref By: ${rx.reference_by || "—"}</p>
          </div>
        </div>
        ${rx.chief_complain ? `<div class="section"><span class="slabel">Chief Complaint</span><p>${rx.chief_complain}</p></div>` : ""}
        ${rx.diagnosis?.length > 0 ? `
        <div class="section"><span class="slabel">Diagnosis</span>
          <table><thead><tr><th>#</th><th>Diagnosis</th><th>Instruction</th></tr></thead><tbody>
            ${rx.diagnosis.map((d, i) => `<tr><td>${i + 1}</td><td><strong>${d.name || "—"}</strong></td><td>${d.instruction || "—"}</td></tr>`).join("")}
          </tbody></table></div>` : ""}
        ${rx.medicine?.length > 0 ? `
        <div class="section"><span class="slabel">Medicines</span>
          <table><thead><tr><th>#</th><th>Medicine</th><th>Type</th><th>Instruction</th><th>Days</th></tr></thead><tbody>
            ${rx.medicine.map((m, i) => `<tr><td>${i + 1}</td><td><strong>${m.name || "—"}</strong></td><td>${m.type || "—"}</td><td>${m.instruction || "—"}</td><td>${m.days ? m.days + "d" : "—"}</td></tr>`).join("")}
          </tbody></table></div>` : ""}
        ${rx.patient_notes ? `<div class="section"><span class="slabel">Notes</span><p>${rx.patient_notes}</p></div>` : ""}
        ${rx.visiting_fees ? `<div class="fees">Visiting Fees: <strong>₹${rx.visiting_fees}</strong></div>` : ""}
      </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(printContent);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 500);
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FaFile size={13} />;
    const ext = filename.split(".").pop().toLowerCase();
    if (ext === "pdf") return <FaFilePdf size={13} color="#e53935" />;
    if (["jpg", "jpeg", "png"].includes(ext)) return <FaFileImage size={13} color="#1e88e5" />;
    return <FaFile size={13} />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const handleAppointmentClick = (appt) => {
    setSelectedAppointment(appt);
    setModalVisible(true);
  };

  const handlePrescriptionClick = (rx) => {
    setSelectedPrescription(rx);
    setPrescriptionModalVisible(true);
  };

  // ✅ updated upload — saves to document table via new endpoint
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("patient_id", user.patient_id);
    setUploading(true);
    try {
      const res = await fetch(`${BASE_URL}/patient/documents/upload`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) { alert(`❌ Server error ${res.status}`); return; }
      const data = await res.json();
      if (data.success) {
        alert("✅ Uploaded successfully");
        // ✅ refresh documents
        const r = await fetch(`${BASE_URL}/patient/documents/${user.patient_id}`);
        const rd = await r.json();
        setDocuments(rd?.documents || []);
      } else {
        alert("❌ Upload failed: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      alert("❌ Error: " + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {

      case "pastAppointments":
        return (
          <div className="tab-content">
            <ul className="appointment-list">
              {pastAppointments.length > 0 ? pastAppointments.map((appt, i) => (
                <li key={i} onClick={() => handleAppointmentClick(appt)} style={{ cursor: "pointer" }}>
                  <span className="serial">{i + 1}</span>
                  <span className="doctor">{appt.doctor_name}</span>
                  <span className="date">{appt.date}</span>
                  <span className="type">{appt.problem}</span>
                </li>
              )) : <p>No past appointments found</p>}
            </ul>
          </div>
        );

      case "appointments":
        return (
          <div className="tab-content">
            <ul className="appointment-list">
              {appointments.length > 0 ? appointments.map((appt, i) => (
                <li key={i} onClick={() => handleAppointmentClick(appt)} style={{ cursor: "pointer" }}>
                  <span className="serial">{i + 1}</span>
                  <span className="doctor">Dr. {appt.doctor_name}</span>
                  <span className="date">{appt.date}</span>
                  <span className="type">{appt.problem}</span>
                </li>
              )) : <p>No upcoming appointments found</p>}
            </ul>
          </div>
        );

        case "documents":
          return (
            <div className="tab-content">
              <ul className="document-list">
                {documents.length > 0 ? documents.map((doc, i) => {
                  const filename = doc.filename || doc.hidden_attach_file?.split("/").pop() || "Document";
                  const url = doc.url || "#";
                  const hasFile = doc.hidden_attach_file && doc.hidden_attach_file !== "";
                 return (
                <li key={doc.id || i}>
                    <span>{filename}</span>
                    {hasFile ? (
                        <button onClick={() => forceDownload(url, filename)}>Download</button>
                    ) : (
                        <span style={{fontSize:"11px", color:"#999"}}>No file</span>
                    )}
                </li>
            );;
                }) : <p>No documents found</p>}
              </ul>
              <button className="upload-btn" onClick={() => fileInputRef.current.click()} disabled={uploading}>
                {uploading ? "Uploading..." : "+ Upload"}
              </button>
              <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
            </div>
          );




      case "prescriptions":
        return (
          <div className="tab-content">
            <ul className="appointment-list">
              {prescriptions.length > 0 ? prescriptions.map((rx, i) => (
                <li
                  key={rx.id || i}
                  onClick={() => handlePrescriptionClick(rx)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="serial">Rx #{i + 1}</span>
                  <span className="doctor">Dr. {rx.doctor_name}</span>
                  <span className="date">{formatDate(rx.date)}</span>
                  <button
                    onClick={(e) => {
                    e.stopPropagation();
                    downloadPrescriptionAsPDF(rx);  // ✅ pass directly
                  }}
                    className="download-btn"  // 👈 same class as document download button
                  >
                    Download
                  </button>
                </li>
              )) : <p>No prescriptions found</p>}
            </ul>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {user ? (
        <div className="patient-profile-container">
          <div className="patient-profile-wrapper">

            {/* LEFT */}
            <div className="profile-left">
              <div className="profile-avatar">
                {user?.picture && user.picture !== "default.png" ? (
                  <img src={`${ASSET_URL}/${user.picture}`} alt="Profile" />
                ) : (
                  <FaUserCircle size={150} color="#b0bec5" />
                )}
                <h4>
                  {user.firstname}
                  <Link to="/update-profile" className="p-1 hover:bg-gray-200 rounded-full inline-flex items-center justify-center">
                    <FaPen className="text-gray-600" size={18} />
                  </Link>
                </h4>
                <p>{user.email}</p>
              </div>
              <div className="profile-info">
                <p><strong>Full Name :</strong> {user.firstname} {user.lastname}</p>
                <p><strong>Email :</strong> {user.email}</p>
                <p><strong>Mobile Number :</strong> {user.mobile}</p>
                <p><strong>Address :</strong> {user.address}</p>
                <p><strong>Gender :</strong> {user.sex}</p>
                <p><strong>Blood Group :</strong> {user.blood_group}</p>
                <p><strong>Date of Birth :</strong> {user.date_of_birth}</p>
              </div>
            </div>

            {/* RIGHT */}
            <div className="profile-right">
              <div className="tabs">
                <button className={activeTab === "pastAppointments" ? "active" : ""} onClick={() => setActiveTab("pastAppointments")}>
                  Past Appointments
                </button>
                <button className={activeTab === "appointments" ? "active" : ""} onClick={() => setActiveTab("appointments")}>
                  Appointments
                </button>
                <button className={activeTab === "documents" ? "active" : ""} onClick={() => setActiveTab("documents")}>
                  Documents
                </button>
                <button className={activeTab === "prescriptions" ? "active" : ""} onClick={() => setActiveTab("prescriptions")}>
                  Prescriptions
                </button>
              </div>
              {renderTabContent()}
            </div>
          </div>

          {/* APPOINTMENT MODAL — completely unchanged */}
          {modalVisible && selectedAppointment && (
            <div
              style={{
                position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
                display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999,
              }}
              onClick={() => setModalVisible(false)}
            >
              <div
                style={{
                  background: "#fff", width: "95%", maxWidth: "700px",
                  borderRadius: "18px", padding: "30px 35px",
                  position: "relative", color: "#000",
                  boxShadow: "0 8px 35px rgba(0,0,0,0.25)",
                  maxHeight: "90vh", overflowY: "auto", scrollbarWidth: "none",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <style>{`div::-webkit-scrollbar { display: none; }`}</style>
                <button
                  onClick={() => setModalVisible(false)}
                  style={{
                    position: "absolute", top: "14px", right: "18px",
                    background: "#e5e5e5", border: "none", padding: "8px 10px",
                    borderRadius: "50%", cursor: "pointer", fontSize: "18px", color: "#444",
                  }}
                >
                  <FaTimes />
                </button>
                <h2 style={{ textAlign: "center", marginBottom: "25px", fontSize: "24px", fontWeight: "700", color: "#333" }}>
                  Appointment Details
                </h2>
                <div style={{ background: "#f9fafb", padding: "18px 20px", borderRadius: "12px", marginBottom: "18px", border: "1px solid #eee" }}>
                  <h3 style={{ marginBottom: "10px", fontSize: "19px" }}>📌 Appointment Info</h3>
                  <p><strong>Appointment ID:</strong> {selectedAppointment.appointment_id}</p>
                  <p><strong>Date:</strong> {selectedAppointment.date}</p>
                  <p><strong>Time Slot:</strong> {selectedAppointment.time_slot}</p>
                  <p><strong>Location:</strong> {selectedAppointment.location_name}</p>
                  <p><strong>Problem:</strong> {selectedAppointment.problem}</p>
                </div>
                <div style={{ background: "#f9fafb", padding: "18px 20px", borderRadius: "12px", marginBottom: "18px", border: "1px solid #eee" }}>
                  <h3 style={{ marginBottom: "10px", fontSize: "19px" }}>👨‍⚕️ Doctor Info</h3>
                  <p><strong>Name:</strong> {selectedAppointment.doctor_name}</p>
                  <p><strong>Specialty:</strong> {selectedAppointment.doctor_specialty}</p>
                </div>
                <div style={{ background: "#f9fafb", padding: "18px 20px", borderRadius: "12px", border: "1px solid #eee" }}>
                  <h3 style={{ marginBottom: "10px", fontSize: "19px" }}>🧑 Patient Info</h3>
                  <p><strong>Patient ID:</strong> {selectedAppointment.patient_id}</p>
                  <p><strong>Name:</strong> {selectedAppointment.patient_name}</p>
                </div>
              </div>
            </div>
          )}

          {/* PRESCRIPTION MODAL — completely unchanged */}
          {prescriptionModalVisible && selectedPrescription && (
            <div
              style={{
                position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
                display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999,
              }}
              onClick={() => setPrescriptionModalVisible(false)}
            >
              <div
                style={{
                  background: "#fff", width: "95%", maxWidth: "700px",
                  borderRadius: "18px", padding: "30px 35px",
                  position: "relative", color: "#000",
                  boxShadow: "0 8px 35px rgba(0,0,0,0.25)",
                  maxHeight: "90vh", overflowY: "auto", scrollbarWidth: "none",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <style>{`div::-webkit-scrollbar { display: none; }`}</style>
                <button
                  onClick={() => setPrescriptionModalVisible(false)}
                  style={{
                    position: "absolute", top: "14px", right: "18px",
                    background: "#e5e5e5", border: "none", padding: "8px 10px",
                    borderRadius: "50%", cursor: "pointer", fontSize: "16px", color: "#444",
                  }}
                >
                  <FaTimes />
                </button>
                <h2 style={{ textAlign: "center", marginBottom: "4px", fontSize: "22px", fontWeight: "700", color: "#333" }}>
                  Prescription
                  <span style={{ fontSize: "14px", fontWeight: "400", color: "#888", marginLeft: "8px" }}>
                    Rx #{prescriptions.indexOf(selectedPrescription) + 1}
                  </span>
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
                  <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px 18px", border: "1px solid #eee" }}>
                    <p style={{ fontSize: "11px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>Doctor Info</p>
                    <p style={{ fontSize: "15px", fontWeight: "700", color: "#1a1a1a", marginBottom: "4px" }}>Dr. {selectedPrescription.doctor_name}</p>
                    <p style={{ fontSize: "12px", color: "#555", marginBottom: "3px" }}>{formatDate(selectedPrescription.date)}</p>
                    <p style={{ fontSize: "12px", color: "#777" }}>
                      {selectedPrescription.appointment_id ? `Appt #${selectedPrescription.appointment_id}` : "Direct Consultation"}
                    </p>
                    {/* {selectedPrescription.patient_type && (
                      <span style={{
                        display: "inline-block", marginTop: "6px",
                        fontSize: "11px", fontWeight: "600",
                        background: selectedPrescription.patient_type === "New" ? "#e8f5e9" : "#fff8e1",
                        color: selectedPrescription.patient_type === "New" ? "#2e7d32" : "#f57f17",
                        padding: "2px 10px", borderRadius: "20px",
                        border: `1px solid ${selectedPrescription.patient_type === "New" ? "#c8e6c9" : "#ffe082"}`,
                      }}>
                        {selectedPrescription.patient_type} Patient
                      </span>
                    )} */}
                  </div>
                  <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px 18px", border: "1px solid #eee" }}>
                    <p style={{ fontSize: "11px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>Patient Info</p>
                    <p style={{ fontSize: "15px", fontWeight: "700", color: "#1a1a1a", marginBottom: "4px" }}>{user?.firstname} {user?.lastname}</p>
                    <p style={{ fontSize: "12px", color: "#555", marginBottom: "3px" }}>DOB: {user?.date_of_birth || "—"}</p>
                    <p style={{ fontSize: "12px", color: "#555", marginBottom: "3px" }}>Gender: {user?.sex || "—"}</p>
                    <p style={{ fontSize: "12px", color: "#555" }}>Blood Group: {user?.blood_group || "—"}</p>
                  </div>
                </div>
                <div style={{ background: "#f9fafb", padding: "14px 18px", borderRadius: "12px", marginBottom: "14px", border: "1px solid #eee" }}>
                  <p style={{ fontSize: "11px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>Visit Info</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "13px", color: "#333" }}>
                    <p><strong>Weight:</strong> {selectedPrescription.weight || "—"}</p>
                    <p><strong>Blood Pressure:</strong> {selectedPrescription.blood_pressure || "—"}</p>
                    <p><strong>Reference By:</strong> {selectedPrescription.reference_by || "—"}</p>
                  </div>
                </div>
                {selectedPrescription.chief_complain && (
                  <div style={{ background: "#f9fafb", padding: "16px 20px", borderRadius: "12px", marginBottom: "14px", border: "1px solid #eee" }}>
                    <h3 style={{ marginBottom: "8px", fontSize: "13px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px" }}>Chief Complaint</h3>
                    <p style={{ fontSize: "14px", color: "#333" }}>{selectedPrescription.chief_complain}</p>
                  </div>
                )}
                {selectedPrescription.diagnosis?.length > 0 && (
                  <div style={{ background: "#f9fafb", padding: "16px 20px", borderRadius: "12px", marginBottom: "14px", border: "1px solid #eee" }}>
                    <h3 style={{ marginBottom: "12px", fontSize: "13px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px" }}>Diagnosis</h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                      <thead>
                        <tr style={{ background: "#f0f0f0" }}>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600", width: "32px" }}>#</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600" }}>Diagnosis</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600" }}>Instruction</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPrescription.diagnosis.map((d, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <td style={{ padding: "8px", color: "#555" }}>{i + 1}</td>
                            <td style={{ padding: "8px", color: "#333", fontWeight: "500" }}>{d.name || "—"}</td>
                            <td style={{ padding: "8px", color: "#555" }}>{d.instruction || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {selectedPrescription.medicine?.length > 0 && (
                  <div style={{ background: "#f9fafb", padding: "16px 20px", borderRadius: "12px", marginBottom: "14px", border: "1px solid #eee" }}>
                    <h3 style={{ marginBottom: "12px", fontSize: "13px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px" }}>Medicines</h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                      <thead>
                        <tr style={{ background: "#f0f0f0" }}>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600", width: "32px" }}>#</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600" }}>Medicine</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600" }}>Type</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600" }}>Instruction</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#555", fontWeight: "600" }}>Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPrescription.medicine.map((med, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <td style={{ padding: "8px", color: "#555" }}>{i + 1}</td>
                            <td style={{ padding: "8px", color: "#333", fontWeight: "500" }}>{med.name || "—"}</td>
                            <td style={{ padding: "8px", color: "#555" }}>{med.type || "—"}</td>
                            <td style={{ padding: "8px", color: "#555" }}>{med.instruction || "—"}</td>
                            <td style={{ padding: "8px", color: "#555" }}>{med.days ? `${med.days}d` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {selectedPrescription.patient_notes && (
                  <div style={{ background: "#f9fafb", padding: "16px 20px", borderRadius: "12px", marginBottom: "14px", border: "1px solid #eee" }}>
                    <h3 style={{ marginBottom: "8px", fontSize: "13px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "0.8px" }}>Notes</h3>
                    <p style={{ fontSize: "14px", color: "#333" }}>{selectedPrescription.patient_notes}</p>
                  </div>
                )}
                {selectedPrescription.visiting_fees && (
                  <div style={{ textAlign: "right", paddingTop: "10px", borderTop: "1px solid #eee" }}>
                    <span style={{ fontSize: "12px", color: "#999" }}>Visiting Fees: </span>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#555" }}>
                      ₹{selectedPrescription.visiting_fees}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      ) : (
        <p>Loading patient data...</p>
      )}
    </div>
  );
}